/**
 * Explicit, bounded context a launcher or producer may attach to one session.
 *
 * The Wizard never derives this object from prompts. Unknown fields are
 * stripped before cloud delivery, and invalid annotations are omitted rather
 * than partially promoted into a receipt-ready claim.
 */

import { createHash } from "node:crypto";
import { realpathSync } from "node:fs";
import { basename, dirname, resolve } from "node:path";

import { z } from "zod";

import { ORGX_SESSION_WORK_CONTEXT_DIR } from "../constants.js";
import {
  deleteFileIfExists,
  readTextIfExists,
  writeJsonFile,
} from "./fs.js";
import { isCredentialFreeHttpsUri } from "./session-summary-contract.js";

export const SESSION_WORK_CONTEXT_VERSION = "orgx-session-work-context/v1";
export const SESSION_WORK_CONTEXT_ENV = "ORGX_SESSION_WORK_CONTEXT";
export const SESSION_WORK_CONTEXT_MAX_JSON_BYTES = 4 * 1024;
export const SESSION_WORK_CONTEXT_ACTIVATION_VERSION =
  "orgx-session-work-context-activation/v2";
export const SESSION_WORK_CONTEXT_LEGACY_ACTIVATION_VERSION =
  "orgx-session-work-context-activation/v1";
export const SESSION_WORK_CONTEXT_ACK_VERSION =
  "orgx-session-work-context-ack/v1";
export const SESSION_WORK_CONTEXT_ACTIVATION_MAX_BYTES = 16 * 1024;
export const SESSION_WORK_CONTEXT_DEFAULT_TTL_MINUTES = 8 * 60;
export const SESSION_WORK_CONTEXT_MAX_TTL_MINUTES = 24 * 60;

export function canonicalSessionWorkContextCwd(cwd: string): string {
  const absolute = resolve(cwd);
  let ancestor = absolute;
  const missing: string[] = [];

  while (true) {
    try {
      return resolve(realpathSync.native(ancestor), ...missing);
    } catch {
      const parent = dirname(ancestor);
      if (parent === ancestor) return absolute;
      missing.unshift(basename(ancestor));
      ancestor = parent;
    }
  }
}

const boundedText = (max: number) => z.string().trim().min(1).max(max);
const currency = boundedText(12).regex(/^[A-Z][A-Z0-9_-]{1,11}$/);

const externalReferenceSchema = z.object({
  system: boundedText(120),
  type: boundedText(120),
  id: boundedText(500),
  uri: boundedText(1_000)
    .refine(
      isCredentialFreeHttpsUri,
      "durable reference URIs must be credential-free HTTPS URLs",
    )
    .optional(),
  version: boundedText(120).optional(),
});

const stringList = (max: number) =>
  z.array(boundedText(max)).max(20).default([]).transform((values) => [
    ...new Set(values),
  ]);

const referenceList = z
  .array(externalReferenceSchema)
  .max(20)
  .default([])
  .transform((refs) => {
    const unique = new Map<string, (typeof refs)[number]>();
    for (const ref of refs) {
      unique.set(`${ref.system}\u0000${ref.type}\u0000${ref.id}`, ref);
    }
    return [...unique.values()].sort((left, right) => {
      const a = `${left.system}\u0000${left.type}\u0000${left.id}`;
      const b = `${right.system}\u0000${right.type}\u0000${right.id}`;
      return a < b ? -1 : a > b ? 1 : 0;
    });
  });

const semanticReferencesSchema = z
  .object({
    context_capsule_ref: externalReferenceSchema.optional(),
    context_delivered_at: z.string().datetime({ offset: true }).optional(),
    decision_refs: referenceList,
    risk_refs: referenceList,
    constraint_refs: referenceList,
    expectation_refs: referenceList,
    learning_refs: referenceList,
    prior_receipt_refs: referenceList,
  })
  .superRefine((value, context) => {
    if (
      Boolean(value.context_capsule_ref) !==
      Boolean(value.context_delivered_at)
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "context_capsule_ref and context_delivered_at must be supplied together",
      });
    }
    const totalRefs =
      value.decision_refs.length +
      value.risk_refs.length +
      value.constraint_refs.length +
      value.expectation_refs.length +
      value.learning_refs.length +
      value.prior_receipt_refs.length +
      (value.context_capsule_ref ? 1 : 0);
    if (totalRefs === 0 || totalRefs > 20) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "semantic_refs must contain between 1 and 20 references",
      });
    }
  });

const authoritySchema = z
  .object({
    mode: z.enum([
      "explicit",
      "delegated",
      "inherited",
      "policy",
      "none",
      "unknown",
    ]),
    status: z.enum(["granted", "restricted", "denied", "expired", "unknown"]),
    scope: z.object({
      actions: stringList(120),
      resources: referenceList,
      systems: stringList(120),
      spend_limit: z
        .object({
          currency,
          amount: z.number().finite().min(0).max(100_000),
        })
        .optional(),
    }),
    authorization_ref: externalReferenceSchema.optional(),
    constraints: stringList(1_000),
    valid_from: z.string().datetime({ offset: true }).optional(),
    valid_until: z.string().datetime({ offset: true }).optional(),
  })
  .refine(
    (value) =>
      !value.valid_from ||
      !value.valid_until ||
      Date.parse(value.valid_until) >= Date.parse(value.valid_from),
    { message: "valid_until must not precede valid_from" },
  );

const availableCostFields = {
  currency,
  total: z.number().finite().min(0).max(100_000),
  estimated: z.boolean().default(true),
  source: boundedText(120).optional(),
};

/**
 * Accept the legacy producer shape while always returning the canonical
 * availability-tagged contract consumed by the app and emitted hook.
 */
const sessionWorkCostInputSchema = z.union([
  z
    .object(availableCostFields)
    .strict()
    .transform((value) => ({
      availability: "available" as const,
      ...value,
    })),
  z
    .object({
      availability: z.literal("available"),
      ...availableCostFields,
    })
    .strict(),
  z
    .object({ availability: z.literal("not_observed") })
    .strict(),
]);

const sessionWorkContextInputSchema = z.object({
  schema_version: z.literal(SESSION_WORK_CONTEXT_VERSION),
  intent: z.object({
    summary: boundedText(2_000),
    objective: boundedText(4_000).optional(),
    acceptance_criteria: stringList(1_000),
    constraints: stringList(1_000),
    request_ref: externalReferenceSchema.optional(),
  }),
  authority: authoritySchema,
  cost: sessionWorkCostInputSchema,
  artifact_refs: referenceList,
  evidence_refs: referenceList,
  semantic_refs: semanticReferencesSchema.optional(),
});

const exactStringList = (max: number) => z.array(boundedText(max)).max(20);
const exactReferenceList = z.array(externalReferenceSchema.strict()).max(20);
const exactSemanticReferencesSchema = z
  .object({
    context_capsule_ref: externalReferenceSchema.strict().optional(),
    context_delivered_at: z.string().datetime({ offset: true }).optional(),
    decision_refs: exactReferenceList,
    risk_refs: exactReferenceList,
    constraint_refs: exactReferenceList,
    expectation_refs: exactReferenceList,
    learning_refs: exactReferenceList,
    prior_receipt_refs: exactReferenceList,
  })
  .strict()
  .superRefine((value, context) => {
    if (Boolean(value.context_capsule_ref) !== Boolean(value.context_delivered_at)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "context_capsule_ref and context_delivered_at must be supplied together",
      });
    }
    const totalRefs =
      value.decision_refs.length +
      value.risk_refs.length +
      value.constraint_refs.length +
      value.expectation_refs.length +
      value.learning_refs.length +
      value.prior_receipt_refs.length +
      (value.context_capsule_ref ? 1 : 0);
    if (totalRefs === 0 || totalRefs > 20) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "semantic_refs must contain between 1 and 20 references",
      });
    }
  });

const exactAuthoritySchema = z
  .object({
    mode: z.enum(["explicit", "delegated", "inherited", "policy", "none", "unknown"]),
    status: z.enum(["granted", "restricted", "denied", "expired", "unknown"]),
    scope: z
      .object({
        actions: exactStringList(120),
        resources: exactReferenceList,
        systems: exactStringList(120),
        spend_limit: z
          .object({
            currency,
            amount: z.number().finite().min(0).max(100_000),
          })
          .strict()
          .optional(),
      })
      .strict(),
    authorization_ref: externalReferenceSchema.strict().optional(),
    constraints: exactStringList(1_000),
    valid_from: z.string().datetime({ offset: true }).optional(),
    valid_until: z.string().datetime({ offset: true }).optional(),
  })
  .strict()
  .refine(
    (value) =>
      !value.valid_from ||
      !value.valid_until ||
      Date.parse(value.valid_until) >= Date.parse(value.valid_from),
    { message: "valid_until must not precede valid_from" },
  );

const exactCostSchema = z.union([
  z
    .object({
      availability: z.literal("available"),
      currency,
      total: z.number().finite().min(0).max(100_000),
      estimated: z.boolean(),
      source: boundedText(120).optional(),
    })
    .strict(),
  z.object({ availability: z.literal("not_observed") }).strict(),
]);

const exactSessionWorkContextSchema = z
  .object({
    schema_version: z.literal(SESSION_WORK_CONTEXT_VERSION),
    provenance: z.literal("producer_asserted"),
    intent: z
      .object({
        summary: boundedText(2_000),
        objective: boundedText(4_000).optional(),
        acceptance_criteria: exactStringList(1_000),
        constraints: exactStringList(1_000),
        request_ref: externalReferenceSchema.strict().optional(),
      })
      .strict(),
    authority: exactAuthoritySchema,
    cost: exactCostSchema,
    artifact_refs: exactReferenceList,
    evidence_refs: exactReferenceList,
    semantic_refs: exactSemanticReferencesSchema.optional(),
  })
  .strict();

export type SessionWorkContext = z.infer<typeof sessionWorkContextInputSchema> & {
  provenance: "producer_asserted";
};

const sessionWorkContextActivationSchema = z.object({
  schema_version: z.literal(SESSION_WORK_CONTEXT_ACTIVATION_VERSION),
  created_at: z.string().datetime({ offset: true }),
  valid_until: z.string().datetime({ offset: true }),
  cwd: boundedText(4_000),
  source_client: boundedText(120),
  session_id: boundedText(500),
  context_sha256: z.string().regex(/^[0-9a-f]{64}$/),
  context: exactSessionWorkContextSchema,
}).strict().refine(
  (value) => Date.parse(value.valid_until) >= Date.parse(value.created_at),
  { message: "valid_until must not precede created_at" },
);

const legacySessionWorkContextActivationSchema = z.object({
  schema_version: z.literal(SESSION_WORK_CONTEXT_LEGACY_ACTIVATION_VERSION),
  created_at: z.string().datetime({ offset: true }),
  valid_until: z.string().datetime({ offset: true }),
  cwd: boundedText(4_000),
  context: sessionWorkContextInputSchema,
}).refine(
  (value) => Date.parse(value.valid_until) >= Date.parse(value.created_at),
  { message: "valid_until must not precede created_at" },
);

export interface SessionWorkContextActivation {
  schema_version: typeof SESSION_WORK_CONTEXT_ACTIVATION_VERSION;
  created_at: string;
  valid_until: string;
  cwd: string;
  source_client: string;
  session_id: string;
  context_sha256: string;
  context: SessionWorkContext;
}

interface LegacySessionWorkContextActivation {
  schema_version: typeof SESSION_WORK_CONTEXT_LEGACY_ACTIVATION_VERSION;
  created_at: string;
  valid_until: string;
  cwd: string;
  context: SessionWorkContext;
}

export type ReadableSessionWorkContextActivation =
  | SessionWorkContextActivation
  | LegacySessionWorkContextActivation;

export type SessionWorkContextActivationState =
  | "ready"
  | "missing"
  | "invalid"
  | "expired"
  | "cwd_mismatch";

export interface SessionWorkContextActivationInspection {
  state: SessionWorkContextActivationState;
  ready: boolean;
  path: string;
  cwd: string;
  activeCwd: string | null;
  createdAt: string | null;
  validUntil: string | null;
  source: "local_file" | null;
  ackVersion: typeof SESSION_WORK_CONTEXT_ACK_VERSION;
  activationVersion: string | null;
  sourceClient: string | null;
  sessionId: string | null;
  contextSha256: string | null;
  context: SessionWorkContext | null;
}

function canonicalJson(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map((item) => canonicalJson(item)).join(",")}]`;
  }
  if (value && typeof value === "object") {
    return `{${Object.entries(value as Record<string, unknown>)
      .filter(([, nested]) => nested !== undefined)
      .sort(([left], [right]) => left < right ? -1 : left > right ? 1 : 0)
      .map(([key, nested]) => `${JSON.stringify(key)}:${canonicalJson(nested)}`)
      .join(",")}}`;
  }
  return JSON.stringify(value);
}

export function sessionWorkContextSha256(value: unknown): string {
  return createHash("sha256").update(canonicalJson(value)).digest("hex");
}

export function normalizeSessionWorkContext(
  value: unknown,
): SessionWorkContext | null {
  const parsed = sessionWorkContextInputSchema.safeParse(value);
  return parsed.success
    ? { ...parsed.data, provenance: "producer_asserted" }
    : null;
}

export function normalizeExactSessionWorkContext(
  value: unknown,
): SessionWorkContext | null {
  const parsed = exactSessionWorkContextSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

export function parseSessionWorkContextEnvironment(
  value: string | undefined,
): SessionWorkContext | null {
  if (
    !value ||
    Buffer.byteLength(value, "utf8") > SESSION_WORK_CONTEXT_MAX_JSON_BYTES
  ) {
    return null;
  }
  try {
    return normalizeSessionWorkContext(JSON.parse(value) as unknown);
  } catch {
    return null;
  }
}

function activationInspection(
  state: SessionWorkContextActivationState,
  path: string,
  cwd: string,
  activation?: ReadableSessionWorkContextActivation,
  requestedIdentity?: { sourceClient: string; sessionId: string },
): SessionWorkContextActivationInspection {
  const scoped = activation?.schema_version === SESSION_WORK_CONTEXT_ACTIVATION_VERSION
    ? activation
    : null;
  return {
    state,
    ready: state === "ready",
    path,
    cwd,
    activeCwd: activation?.cwd ?? null,
    createdAt: activation?.created_at ?? null,
    validUntil: activation?.valid_until ?? null,
    source: activation ? "local_file" : null,
    ackVersion: SESSION_WORK_CONTEXT_ACK_VERSION,
    activationVersion: activation?.schema_version ?? null,
    sourceClient: scoped?.source_client ?? requestedIdentity?.sourceClient ?? null,
    sessionId: scoped?.session_id ?? requestedIdentity?.sessionId ?? null,
    contextSha256: scoped?.context_sha256 ?? (
      activation ? sessionWorkContextSha256(activation.context) : null
    ),
    context: state === "ready" ? activation?.context ?? null : null,
  };
}

export function createSessionWorkContextActivation(options: {
  context: unknown;
  cwd?: string;
  now?: Date;
  ttlMinutes?: number;
  sourceClient?: string;
  sessionId?: string;
  expectedContextSha256?: string;
}): ReadableSessionWorkContextActivation {
  const scoped = Boolean(options.sourceClient && options.sessionId);
  const context = scoped
    ? normalizeExactSessionWorkContext(options.context)
    : normalizeSessionWorkContext(options.context);
  if (!context) {
    throw new Error("Session work context does not match orgx-session-work-context/v1.");
  }
  if (
    Buffer.byteLength(JSON.stringify(context), "utf8") >
    SESSION_WORK_CONTEXT_MAX_JSON_BYTES
  ) {
    throw new Error(
      `Session work context exceeds the ${SESSION_WORK_CONTEXT_MAX_JSON_BYTES}-byte canonical limit.`,
    );
  }
  const ttlMinutes = options.ttlMinutes ?? SESSION_WORK_CONTEXT_DEFAULT_TTL_MINUTES;
  if (
    !Number.isInteger(ttlMinutes) ||
    ttlMinutes < 1 ||
    ttlMinutes > SESSION_WORK_CONTEXT_MAX_TTL_MINUTES
  ) {
    throw new Error(
      `Session work context TTL must be between 1 and ${SESSION_WORK_CONTEXT_MAX_TTL_MINUTES} minutes.`,
    );
  }
  const now = options.now ?? new Date();
  if (Boolean(options.sourceClient) !== Boolean(options.sessionId)) {
    throw new Error("Session work context source client and session id must be supplied together.");
  }
  const sourceClient = options.sourceClient?.trim();
  const sessionId = options.sessionId?.trim();
  if (sourceClient !== undefined && (!sourceClient || sourceClient.length > 120)) {
    throw new Error("Session work context source client must be 1 to 120 characters.");
  }
  if (sessionId !== undefined && (!sessionId || sessionId.length > 500)) {
    throw new Error("Session work context session id must be 1 to 500 characters.");
  }
  const contextSha256 = sessionWorkContextSha256(context);
  if (
    options.expectedContextSha256 &&
    options.expectedContextSha256.toLowerCase() !== contextSha256
  ) {
    throw new Error("Session work context fingerprint does not match the canonical accepted context.");
  }
  const common = {
    created_at: now.toISOString(),
    valid_until: new Date(now.getTime() + ttlMinutes * 60_000).toISOString(),
    cwd: canonicalSessionWorkContextCwd(options.cwd ?? process.cwd()),
    context,
  };
  return sourceClient && sessionId
    ? {
        schema_version: SESSION_WORK_CONTEXT_ACTIVATION_VERSION,
        ...common,
        source_client: sourceClient,
        session_id: sessionId,
        context_sha256: contextSha256,
      }
    : {
        schema_version: SESSION_WORK_CONTEXT_LEGACY_ACTIVATION_VERSION,
        ...common,
      };
}

export function writeSessionWorkContextActivation(
  activation: ReadableSessionWorkContextActivation,
  path = sessionWorkContextActivationPath(
    activation.cwd,
    undefined,
    activation.schema_version === SESSION_WORK_CONTEXT_ACTIVATION_VERSION
      ? {
          sourceClient: activation.source_client,
          sessionId: activation.session_id,
        }
      : undefined,
  ),
): void {
  writeJsonFile(path, activation, { mode: 0o600 });
}

export function clearSessionWorkContextActivation(
  cwd = process.cwd(),
  path = sessionWorkContextActivationPath(cwd),
): boolean {
  return deleteFileIfExists(path);
}

export function sessionWorkContextActivationPath(
  cwd = process.cwd(),
  directory = ORGX_SESSION_WORK_CONTEXT_DIR,
  identity?: { sourceClient: string; sessionId: string },
): string {
  const key = createHash("sha256")
    .update(
      identity
        ? canonicalJson([
            canonicalSessionWorkContextCwd(cwd),
            identity.sourceClient.trim(),
            identity.sessionId.trim(),
          ])
        : canonicalSessionWorkContextCwd(cwd),
    )
    .digest("hex");
  return resolve(directory, `${key}.json`);
}

export function inspectSessionWorkContextActivation(options: {
  path?: string;
  cwd?: string;
  now?: Date;
  sourceClient?: string;
  sessionId?: string;
} = {}): SessionWorkContextActivationInspection {
  const cwd = canonicalSessionWorkContextCwd(options.cwd ?? process.cwd());
  const identity = options.sourceClient && options.sessionId
    ? { sourceClient: options.sourceClient, sessionId: options.sessionId }
    : undefined;
  const path = options.path ?? sessionWorkContextActivationPath(
    cwd,
    undefined,
    identity,
  );
  const raw = readTextIfExists(path);
  if (raw === null) return activationInspection("missing", path, cwd, undefined, identity);
  if (Buffer.byteLength(raw, "utf8") > SESSION_WORK_CONTEXT_ACTIVATION_MAX_BYTES) {
    return activationInspection("invalid", path, cwd, undefined, identity);
  }
  let value: unknown;
  try {
    value = JSON.parse(raw) as unknown;
  } catch {
    return activationInspection("invalid", path, cwd, undefined, identity);
  }
  const parsed = sessionWorkContextActivationSchema.safeParse(value);
  let data: ReadableSessionWorkContextActivation;
  if (parsed.success) {
    data = { ...parsed.data, context: { ...parsed.data.context, provenance: "producer_asserted" } };
  } else {
    const legacyParsed = legacySessionWorkContextActivationSchema.safeParse(value);
    if (!legacyParsed.success) {
      return activationInspection("invalid", path, cwd, undefined, identity);
    }
    data = {
      ...legacyParsed.data,
      context: { ...legacyParsed.data.context, provenance: "producer_asserted" },
    };
  }
  const activation: ReadableSessionWorkContextActivation = {
    ...data,
    cwd: canonicalSessionWorkContextCwd(data.cwd),
  };
  if (Date.parse(activation.valid_until) <= (options.now ?? new Date()).getTime()) {
    return activationInspection("expired", path, cwd, activation, identity);
  }
  if (activation.cwd !== cwd) {
    return activationInspection("cwd_mismatch", path, cwd, activation, identity);
  }
  if (activation.schema_version === SESSION_WORK_CONTEXT_ACTIVATION_VERSION) {
    if (
      activation.context_sha256 !== sessionWorkContextSha256(activation.context) ||
      (options.sourceClient && activation.source_client !== options.sourceClient.trim()) ||
      (options.sessionId && activation.session_id !== options.sessionId.trim())
    ) {
      return activationInspection("invalid", path, cwd, activation, identity);
    }
  } else if (identity) {
    return activationInspection("invalid", path, cwd, activation, identity);
  }
  return activationInspection("ready", path, cwd, activation, identity);
}
